'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowLeft, Play, Eye, ExternalLink } from 'lucide-react';
import StandardLayout from '../../components/layout/StandardLayout';
import Button from '../../components/ui/Button';
import '../../components/editor/Documentation.scss';
import '../../components/ui/Card.scss';
import '../../components/ui/Loading.scss';

interface Example {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  tags: string[];
  filename: string;
}

export default function ExamplesPage() {
  const [examples, setExamples] = useState<Example[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('All');

  useEffect(() => {
    fetchExamples();
  }, []);

  const fetchExamples = async () => {
    try {
      const response = await fetch('/api/examples');
      const data = await response.json();
      setExamples(data.examples || []);
    } catch (error) {
      console.error('Error fetching examples:', error);
      // Fallback examples if API fails
      setExamples([
        {
          id: 'simple-square',
          title: 'Simple Square',
          description: 'A basic square shape to get started with FullControl',
          difficulty: 'Beginner',
          tags: ['basic', 'geometry'],
          filename: 'simple_square.py'
        },
        {
          id: 'parametric-flower',
          title: 'Parametric Flower',
          description: 'Create beautiful flower patterns with customizable parameters',
          difficulty: 'Intermediate',
          tags: ['parametric', 'artistic'],
          filename: 'parametric_flower.py'
        },
        {
          id: 'spiral-vase',
          title: 'Spiral Vase',
          description: 'Non-planar spiral vase demonstrating advanced techniques',
          difficulty: 'Advanced',
          tags: ['non-planar', 'vase'],
          filename: 'spiral_vase.py'
        },
        {
          id: 'gear-structure',
          title: 'Gear Structure',
          description: 'Functional gear with precise geometry and tolerances',
          difficulty: 'Advanced',
          tags: ['functional', 'mechanical'],
          filename: 'gear_structure.py'
        },
        {
          id: 'multi-layer-cylinder',
          title: 'Multi-Layer Cylinder',
          description: 'Learn layer management with a simple cylinder',
          difficulty: 'Beginner',
          tags: ['layers', 'basic'],
          filename: 'multi_layer_cylinder.py'
        },
        {
          id: 'sine-wave-pattern',
          title: 'Sine Wave Pattern',
          description: 'Mathematical patterns using trigonometric functions',
          difficulty: 'Intermediate',
          tags: ['mathematical', 'patterns'],
          filename: 'sine_wave_pattern.py'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredExamples = selectedDifficulty === 'All'
    ? examples
    : examples.filter(ex => ex.difficulty === selectedDifficulty);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return '#28a745';
      case 'Intermediate': return '#ffc107';
      case 'Advanced': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          {/* Navigation */}
          <nav className="docs-nav">
            <Link href="/docs" className="back-link">
              <ArrowLeft size={16} />
              Back to Documentation
            </Link>
          </nav>

          {/* Header */}
          <header className="docs-header">
            <h1>Examples & Tutorials</h1>
            <p>Learn FullControl through practical examples</p>
          </header>

          {/* Content */}
          <div className="docs-content">
            <section className="docs-section">
              <h2>Browse Examples</h2>
              <p>
                Explore a collection of FullControl examples ranging from simple shapes to complex
                parametric designs. Each example includes detailed explanations and can be loaded
                directly into the editor.
              </p>

              {/* Filter */}
              <div className="example-filters">
                <label>Filter by difficulty:</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="difficulty-filter"
                >
                  <option value="All">All Levels</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              {/* Examples Grid */}
              {loading ? (
                <div className="loading-examples">Loading examples...</div>
              ) : (
                <div className="examples-grid">
                  {filteredExamples.map((example) => (
                    <div key={example.id} className="example-card">
                      <div className="example-header">
                        <h3>{example.title}</h3>
                        <span
                          className="difficulty-badge"
                          style={{ backgroundColor: getDifficultyColor(example.difficulty) }}
                        >
                          {example.difficulty}
                        </span>
                      </div>
                      <p className="example-description">{example.description}</p>
                      <div className="example-tags">
                        {example.tags.map((tag) => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>                      <div className="example-actions">
                        <Button
                          href={`/create?example=${example.id}`}
                          type="primary"
                          icon={Play}
                        >
                          Try Example
                        </Button>
                        <Link
                          href={`/examples/${example.filename}`}
                          className="btn btn-outline"
                        >
                          <Eye size={16} />
                          View Code
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="docs-section">
              <h2>Tutorial Series</h2>
              <p>
                Step-by-step tutorials that build upon each other to teach FullControl concepts progressively.
              </p>

              <div className="tutorial-series">
                <div className="tutorial-card">
                  <h3>🏁 Getting Started Series</h3>
                  <p>Perfect for complete beginners to FullControl</p>
                  <ol>
                    <li><Link href="/docs/getting-started" className="tutorial-link">Your First Sketch</Link></li>
                    <li><Link href="/docs/basics" className="tutorial-link">Understanding Points and Steps</Link></li>
                    <li><span className="tutorial-link disabled">Working with Layers</span></li>
                    <li><span className="tutorial-link disabled">Adding Printer Controls</span></li>
                  </ol>
                </div>

                <div className="tutorial-card">
                  <h3>🔧 Intermediate Techniques</h3>
                  <p>Build more complex designs with advanced features</p>
                  <ol>
                    <li><span className="tutorial-link disabled">Parametric Design Patterns</span></li>
                    <li><span className="tutorial-link disabled">Custom Infill Structures</span></li>
                    <li><span className="tutorial-link disabled">Multi-Material Printing</span></li>
                    <li><span className="tutorial-link disabled">Speed and Temperature Control</span></li>
                  </ol>
                </div>

                <div className="tutorial-card">
                  <h3>🚀 Advanced Topics</h3>
                  <p>Push the boundaries of what's possible</p>
                  <ol>
                    <li><span className="tutorial-link disabled">Non-Planar Printing</span></li>
                    <li><span className="tutorial-link disabled">Functional Mechanical Parts</span></li>
                    <li><span className="tutorial-link disabled">Mathematical Art and Patterns</span></li>
                    <li><span className="tutorial-link disabled">Integration with External Libraries</span></li>
                  </ol>
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>External Resources</h2>
              <p>
                Additional learning materials and inspiration from the broader FullControl community.
              </p>

              <div className="external-resources">
                <a
                  href="https://github.com/FullControlXYZ/fullcontrol/tree/master/tutorials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  <span>Official Jupyter Notebook Tutorials</span>
                  <ExternalLink size={16} />
                </a>
                <a
                  href="https://fullcontrol.xyz/examples"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  <span>FullControl Examples Gallery</span>
                  <ExternalLink size={16} />
                </a>
                <a
                  href="https://github.com/FullControlXYZ/fullcontrol/discussions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  <span>Community Discussions</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            </section>

            <section className="docs-section">
              <h2>Contribute an Example</h2>
              <p>
                Have a great FullControl design you'd like to share? We welcome community contributions!
                Examples help others learn and showcase the capabilities of the platform.
              </p>
              <div className="contribute-info">
                <h4>What makes a good example:</h4>
                <ul>
                  <li>Clear, well-commented code</li>
                  <li>Demonstrates a specific concept or technique</li>
                  <li>Includes parameter explanations</li>
                  <li>Works reliably with common printer settings</li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </div>
    </StandardLayout>
  );
}
